import { type QueryFunctionContext, useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import useTranslation from '@hooks/useTranslation';

import { apiClient } from '@services/apiClient';

import {
  type ITopFailureCauseCategories,
  type ITopFailureCauseCategoriesKeys,
} from '../model/topFailureCauseCategories';

const topFailureCauseCategoriesKey = (
  keys: ITopFailureCauseCategoriesKeys,
): [string, Array<number>, string] => ['topFailureCauseCategories', keys.locationIds, keys.statusType];

const topFailureCauseCategoriesFn = ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof topFailureCauseCategoriesKey>>) => {
  return apiClient.get<{ data: ITopFailureCauseCategories[] }>(
    `${APP_API_URL_V2}fsd/action-item/executive-summary/top-failure-cause-categories`,
    {
      params: {
        location_ids: queryKey[1],
        status_type: queryKey[2] === 'all' ? undefined : queryKey[2],
      },
    },
  );
};

const topFailureCauseCategoriesSelect = (data: ITopFailureCauseCategories[], t: (text: string) => string) => {
  if (!data) {
    return [];
  }
  return data.map(
    (item): Highcharts.PointOptionsObject => ({
      name: t(item.fault_name),
      y: item.action_items_count,
    }),
  );
};

export const useTopFailureCauseCategoriesQuery = (keys: ITopFailureCauseCategoriesKeys) => {
  const { t } = useTranslation();
  return useQuery({
    queryKey: topFailureCauseCategoriesKey(keys),
    queryFn: topFailureCauseCategoriesFn,
    select: ({ data }: AxiosResponse<{ data: ITopFailureCauseCategories[] }>) =>
      topFailureCauseCategoriesSelect(data.data, t),
  });
};
